import React from 'react';
import { formatCurrency } from '../../utils/formatCurrency';
import { roundMoney, toMoneyNumber } from '../../utils/money';
import useUnreadableNumberEntry from './useUnreadableNumberEntry';

/**
 * THE money input. Shows the formatted amount at rest, the raw number while
 * you are typing in it.
 *
 * ═════ WHY THIS FILE EXISTS (2026-09-20, job-list C10) ═════
 * `CurrencyInput` was defined TWICE — `pages/BudgetCostCodes.js:356` and
 * `pages/MarketplaceCreateListing.js:13` — an undeclared breach of the Code
 * Unification Principle. Same move, same reasoning and the same precedent as
 * `ui/SearchInput.js`, which was generalised out of `components/gags/
 * GagSearchInput.js` on 2026-08-08 (see `ui/CLAUDE.md`'s Shared Modules entry
 * for it): nothing about either copy was page-specific except its dressing.
 *
 * 🚨 **THE TWO COPIES WERE NOT THE SAME COMPONENT, AND THE UNIFICATION IS NOT
 * A NO-OP. MEASURED BEFORE ANY CODE WAS WRITTEN, NOT ASSUMED.** They agreed on
 * what they emit to `onChange` (a raw STRING, `e.target.value`, never a number
 * and never the event) and on every well-formed value — `1000`, `"1000"`, `0`,
 * `"0"`, `""`, `null`, `undefined`, `-50`, `"-50"`, `"1e3"` all formatted
 * identically. They disagreed on FIVE axes, and two of those disagreements had
 * to be RULED rather than merged:
 *
 *   1. DOM shape — Budget rendered a bare `<input>`; Marketplace wrapped it in
 *      a `relative` div with an absolutely-positioned `£` and a `pl-7`.
 *   2. Malformed input — see THE MALFORMED-VALUE RULING below.
 *   3. Props — Budget had `className`/`disabled`/`allowNegative`; Marketplace
 *      had neither of the first two, hardcoded its classes, and added
 *      `id`/`name`.
 *   4. Placeholder — Budget passed it verbatim; Marketplace forced defaults
 *      through a `||`.
 *   5. The symbol. See THE PREFIX CONTRACT below; this one was a live defect.
 *
 * ═════ 🚨 THE PREFIX CONTRACT — WHY `prefix` AND `includeSymbol` ARE ONE ═════
 * `prefix` renders a `£` as a separate glyph sitting inside the field's left
 * padding, which is the Marketplace form's look. **When it is ON, the input's
 * own value MUST NOT carry a symbol of its own**, so the blurred value is
 * built with `formatCurrency(n, false)` — the second positional argument is
 * `includeSymbol` and it defaults `true` (`utils/formatCurrency.js`).
 *
 * 🔑 **THE TWO HALVES ARE WELDED TOGETHER IN ONE EXPRESSION DELIBERATELY, AND
 * THAT IS THE WHOLE FIX.** Before unification the Marketplace copy rendered
 * the span AND called `formatCurrency` with its default symbol, so a populated,
 * unfocused Daily Rate read **"£ £1,000.00"** — measured in a real render, not
 * inferred. jsdom evidences the two symbols but NOT their overlap, and no
 * stylesheet runs in this suite, which is why 6,112 green tests never saw it.
 * Patching the call site would have left the next caller who opts into `prefix`
 * free to reintroduce it; deriving `includeSymbol` FROM `prefix` makes a double
 * symbol structurally impossible. **Never pass an independent `includeSymbol`
 * into this component, and never render the span from a call site.**
 *
 * ═════ 🪤 THE MALFORMED-VALUE RULING (owner-level, 2026-09-20) ═════
 * The two copies genuinely disagreed here and one of them was wrong:
 *
 *      "12abc"   Budget "£12.00"   Marketplace "£NaN"
 *      "abc"     Budget "abc"      Marketplace "£NaN"
 *      "1.5.5"   Budget "£1.50"    Marketplace "£NaN"
 *
 * **BUDGET'S BEHAVIOUR WON.** `parseFloat`, and when that yields `NaN` the
 * user's own text is handed straight back. `£NaN` is never a correct thing to
 * show in a money field, and raw text is the recoverable answer — you can see
 * what you typed and fix it. 🪤 The `£NaN` came from `formatCurrency`'s
 * `Number(amount || 0)`, which is a separate piece of owed work on a shared
 * file; do not "fix" it from here.
 *
 * 🔻 **THIS SAID "YOU CANNOT REACH IT BY TYPING" UNTIL 2026-09-23 AND THAT WAS
 * FALSE.** The ruling still governs a value a CALLER hands to `value` (a
 * hydrated form, a drop). What TYPING produces — see THE badInput RULE below.
 *
 * ═════ 🚨 THE badInput RULE (2026-09-23) — MALFORMED IS NEVER BLANK ═════
 * MEASURED in a 3-engine Playwright replica. **It is a THREE-engine path**:
 * Firefox reports `value: ""` + `validity.badInput: true` for `1,000`, `12abc`
 * and `1.2.3` while the box still SHOWS the typed text; Chromium and WebKit
 * filter those keystrokes but report the same `""` + badInput for the PARTIALS
 * `-`, `1e` and `.`. This control used to pass that `""` up, every caller read
 * it as BLANK, and on a nullable column blank is `null` — **a stored £1,234.56
 * estimate was CLEARED with a 200** — while on a NOT NULL column blank was a
 * silent £0.
 *
 * 🔑 The fix is here and nowhere else: on `badInput` the control emits
 * `MALFORMED_AMOUNT`, a non-blank string `isMalformedAmount`
 * (`utils/moneyPayload.js`) answers TRUE for, so every caller's existing
 * validator refuses the save CLIENT-side with its own inline message — on
 * nullable and NOT NULL columns alike. 🚨 **That validator is the GUARD; the
 * server's by-name refusal is only the BACKSTOP** — and on a nullable column
 * not even that, because `toPayloadAmount` maps malformed AND blank to the same
 * `null` there. Never remove a caller's `isMalformedAmount` check. Callers
 * (5 files): HireActionModal, GagDetailPanel, BudgetCostCodes,
 * MarketplaceCreateListing, OrderForm (`unit_price`/`hire_rate`, validated by
 * `utils/orderFormHelpers.js#validateForm`).
 *
 * 🔀 **THE MECHANISM LIVES IN `./useUnreadableNumberEntry.js` SINCE 2026-10-28**
 * — extracted verbatim so `ui/NumberInput` (the non-money twin) shares it
 * rather than copying it: the marker shown as `''` while focused, `onInput`
 * beside `onChange`, the F1 exit-then-re-entry `change` ignore and the F4
 * Backspace clear. Its docblock carries the three measurements. This control
 * passes `MALFORMED_AMOUNT` as the marker; its DOM is byte-identical.
 *
 * ═════ 🪤 `testId` AND `id`/`name` ARE THREE CONCERNS, NOT ONE SPELLED THRICE ═════
 * **Do not tidy them into one prop.** `id` is load-bearing HTML — the
 * Marketplace form's `<label htmlFor="listing-daily-rate">` gets this control's
 * ACCESSIBLE NAME from it, so collapsing it into a test hook breaks a screen
 * reader. `name` is form semantics. `testId` renders `data-testid` and exists
 * only for the harness. A control can legitimately need all three at once, and
 * two of the three are user-facing while the third is not.
 *
 * ═════ 🔑 OPT-IN, DEFAULT OFF — THE HOUSE CONVENTION FOR EXTENDING THIS ═════
 * `allowNegative` (2026-08-15), `testId` (2026-08-23) and now `prefix`
 * (2026-09-20) are all opt-in and all default to the pre-existing behaviour, so
 * every existing caller renders BYTE-IDENTICALLY and no call site had to be
 * touched to add them. **Any future prop follows the same rule.** It is what
 * makes a shared control safe to extend when one of its callers has thirty-odd
 * money inputs on one screen. React omits an attribute entirely for `undefined`,
 * so an unset `id`/`name`/`testId` costs a caller nothing — not even an empty
 * attribute.
 *
 * 🪤 `min="0"` is a NATIVE HINT a browser enforces only inside a `<form>`
 * submit. It is not what makes "no negative money" real — the pre-save checks
 * are (`pages/BudgetCostCodes.noNegativeMoney.test.js` and the server's own
 * refusal). Do not weaken a pre-save check because this attribute is here.
 *
 * ═════ 🚨 THE DROP RULING (2026-09-21) — A DROP REPLACES, A PASTE DOES NOT ═════
 * Measured by the product owner in a real browser: dragging the text `1,000`
 * onto a Budget money field holding `0` left the field reading **`01000`**. The
 * browser's default action for a drop on a text control is INSERT-AT-CARET, and
 * a money field has nothing to insert into. `0` + `1000` is harmless because
 * `parseFloat('01000')` is 1000; the same drop onto a field reading `50`
 * produces `501000`, a wrong rate nobody proof-reads because a drop does not
 * look like typing.
 *
 * **REPLACE IS THE RULING, AND THE ARGUMENT IS ABOUT AIM, NOT ABOUT TASTE.** A
 * paste keeps insert-at-caret because the user PLACED that caret — with a click
 * or with select-all — so inserting is literally what they asked for. A drop has
 * no caret the user chose: the insertion point is wherever the pointer happened
 * to be released, so insert-at-caret makes the resulting number a function of a
 * few pixels (`501000`, `100050` and `510000` are all reachable from the same
 * gesture). Replacement is the only reading of "I dragged a number onto a price
 * box" that is deterministic, and it is also the recoverable one — the old
 * amount is one retype away, whereas a concatenated one looks like a number and
 * passes every check downstream.
 *
 * 🚨 **`preventDefault()` TAKES THE BROWSER'S OWN SANITISER OFF THE PATH, AND
 * THAT IS THE DANGEROUS PART OF THIS FIX.** The reason the owner saw `01000`
 * and not `0` + `1,000` is that a focused field here is a real `type="number"`,
 * and the browser ate the comma. Claim the drop and that stops happening — so
 * `moneyFromDroppedText` below MUST strip the separators itself. A handler
 * written as the obvious `onChange(e.dataTransfer.getData('text/plain'))` hands
 * `'1,000'` to a caller that will `parseFloat` it into **`1`**, turning a
 * cosmetic defect into a live thousandfold under-price. `CurrencyInput.test.js`
 * §4.2 is the assertion that forbids it; do not "simplify" past it.
 *
 * 🪤 **A FILE DROP MUST STILL BE CLAIMED, AND MUST NOT WIPE THE FIELD.**
 * `getData('text/plain')` is `''` for a dragged file, so emitting it
 * unconditionally would clear a rate the user had typed. The `preventDefault`
 * stays unconditional even so: an unclaimed file drop makes the browser
 * NAVIGATE THE TAB to that file and take the half-filled form with it — the
 * same reasoning as the drop zones in `components/OrderInlineEditor.js`.
 *
 * ═════ 🚨 THE BLUR SNAP (2026-09-22) — WHAT YOU SEE IS WHAT IS STORED ═════
 * Owner: *"the box shows the 2dp value so all sums are correct based on what
 * the user sees and if they were to manually add them up on a calculator. What
 * I don't want is the user to type in a number that is visible and doesn't snap
 * to 2dp yet the backend changes the totals."*
 *
 * Until this change the resting state formatted for DISPLAY ONLY: typing
 * `19.3333333` showed `£19.33` while the caller's state — and therefore the
 * request body and the stored column — still held `19.3333333`. The box and the
 * database disagreed on every money field in the app, invisibly.
 *
 * 🚨 **`roundMoney` FROM `utils/money.js`, NEVER `toFixed(2)`.** MEASURED, not
 * assumed: `1.335` is **1.34** under the house rule and `"1.33"` under
 * `toFixed`; so are `1.005` (1.01 vs 1.00), `0.145` (0.15 vs 0.14) and `2.675`
 * (2.68 vs 2.67). `utils/money.js` is the byte-identical client MIRROR of
 * `server/helpers/money.js`, held in step by
 * `server/tests/regressions/money-client-parity.test.js` — **and that parity is
 * the whole licence for snapping here**, because it is what makes the number in
 * the box and the number the server will store the SAME number. A `toFixed`
 * would put a second money rule in the browser, in the one place a user can see
 * it. `CurrencyInput.test.js` §5.2 forbids it.
 *
 * 🪤 **THREE VALUES DO NOT MOVE, and each is a ruling rather than a taste.**
 *   - BLANK STAYS BLANK. `''` must never become `'0.00'`: every caller's
 *     payload builder reads blank as "take the column's default"
 *     (`utils/moneyPayload.js`), so inventing a zero here changes what is
 *     WRITTEN, not merely what is shown.
 *   - A REAL TYPED `0` SURVIVES as `£0.00`. £0 is money by owner ruling, never
 *     an absence (`utils/moneyOrAbsent.js`). The snap must not blank it, and
 *     nothing in this handler may use `||`.
 *   - MALFORMED TEXT IS HANDED BACK. `roundMoney('abc')` is `null`, and
 *     `String(null)` is the literal `'null'` — so the snap emits NOTHING on a
 *     `null` and THE MALFORMED-VALUE RULING above keeps holding on this path.
 *
 * 🔑 **THE SNAP IS CONDITIONAL, AND THAT IS A CONTRACT, NOT AN OPTIMISATION.**
 * It emits only when the rounded number DIFFERS from the number already held.
 * ~45 call sites across eight files hold these values as NUMBERS as often as
 * strings, so an unconditional emit would rewrite a caller's `50` into `'50'`
 * on every blur of a field nobody touched — tripping a dirty guard for no
 * change. §5.9 pins it.
 *
 * 🪤 **A DISABLED CONTROL IS NEVER SNAPPED** (§5.13). Nothing may write through
 * a control the caller has switched off — the same reasoning that makes
 * `handleDrop` return early on `disabled`.
 *
 * 🪤 jsdom has no layout, so none of §5 proves the box LOOKS right. What it
 * pins is the CONTRACT — what reached `onChange` and what the field then read.
 * **The owner's calculator test is a BROWSER check and stays owed.**
 *
 * 🔑 **`onPaste` IS DELIBERATELY ABSENT AND MUST STAY ABSENT.** The owner
 * measured paste as correct on the same day. Pinned in both directions by
 * `CurrencyInput.test.js` §4.13-§4.15, which assert the paste event is NOT
 * `defaultPrevented` and that the browser's composed value flows through
 * untouched. `onDragOver` is absent too, and for a duller reason: an `<input>`
 * is already a native drop target, so nothing needs to enable the drop.
 *
 * @param {string|number|null|undefined} value   Controlled value. `0` is a
 *   legitimate amount and is never the absence marker — hence `??`, never `||`.
 * @param {(next: string) => void} onChange      Receives the raw input STRING.
 * @param {string} [placeholder]                 Passed through VERBATIM; an
 *   omitted placeholder renders no placeholder.
 * @param {string} [className]                   The caller's field styling.
 * @param {boolean} [disabled]
 * @param {boolean} [allowNegative=false]        Drops `min="0"`.
 * @param {string} [testId]                      `data-testid`.
 * @param {string} [id]                          Real HTML id; `htmlFor` target.
 * @param {string} [name]                        Real HTML form name.
 * @param {boolean} [prefix=false]               Render a standalone `£` glyph;
 *   also strips the symbol from the value. See THE PREFIX CONTRACT.
 */

/**
 * The dropped text, reduced to something a money field can hold. Strips the
 * currency symbol, the thousands separators and any whitespace; hands back the
 * user's own trimmed text when what is left does not parse. See THE DROP RULING.
 *
 * 🚨 The separator strip is NOT cosmetic — it is what stands in for the
 * browser sanitiser that `preventDefault()` removed. Read the ruling before
 * touching this regex.
 */
const DROP_SEPARATORS = /[\s,£$€]/g;

/**
 * What a focused field emits when the browser reports `validity.badInput` —
 * see THE badInput RULE. Non-blank and non-numeric, so `isMalformedAmount` is
 * TRUE for it; it starts with a letter, so `parseFloat` is `NaN` and no
 * sign-first check (`crewMoneyFormError`) can read a number out of it. It is
 * shown at rest, so it reads as an error message.
 */
export const MALFORMED_AMOUNT = 'Invalid amount';

function moneyFromDroppedText(text) {
  const trimmed = String(text).trim();
  const cleaned = trimmed.replace(DROP_SEPARATORS, '');
  // 🪤 Unparseable text is handed back VERBATIM, not blanked — a drop is now a
  // way to get a malformed value in here, so THE MALFORMED-VALUE RULING above
  // has to hold on this path too. `parseFloat` answers NaN, every caller's
  // pre-save check refuses it, and the user can see what landed and fix it.
  return isNaN(parseFloat(cleaned)) ? trimmed : cleaned;
}

function CurrencyInput({
  value,
  onChange,
  placeholder,
  className,
  disabled,
  allowNegative = false,
  testId,
  id,
  name,
  prefix = false,
}) {
  const {
    focused, setFocused, rawValue, isMarker, focusedDisplay, emitTyped, handleKeyDown, dropHandler,
  } = useUnreadableNumberEntry({ value, onChange, marker: MALFORMED_AMOUNT, disabled });
  const numericValue = parseFloat(rawValue);
  // eslint-disable-next-line no-nested-ternary
  const displayValue = focused
    // 🪤 `''` for the marker — see THE badInput RULE; never "simplify" it away.
    ? focusedDisplay
    : rawValue === ''
      ? rawValue
      // 🚨 `!prefix` IS `includeSymbol`. Welded — see THE PREFIX CONTRACT.
      : !isNaN(numericValue) ? formatCurrency(numericValue, !prefix) : rawValue;

  // The glyph belongs to the RESTING state only: while focused the control is
  // a real `type="number"` and the browser's own spinner/caret own that box.
  // Never beside the marker: `£ Invalid amount` reads as a price.
  const showPrefix = prefix && !focused && !isMarker;

  /**
   * See THE DROP RULING. The claim (unconditional `preventDefault`, a file drop
   * emits nothing) is the shared hook's `dropHandler`; what the text BECOMES is
   * money's own rule, `moneyFromDroppedText`.
   */
  const handleDrop = dropHandler(moneyFromDroppedText);

  /**
   * See THE BLUR SNAP. Order matters only in that the emit comes FIRST: React
   * batches both state updates into one render, so the caller's new 2dp value
   * and `focused === false` arrive together and the field paints the formatted
   * amount once, never the raw number for a frame.
   *
   * 🪤 `rounded === numeric` compares NUMBERS, so a caller holding `50` and a
   * caller holding `'50'` are both left alone — the emit fires on a genuine
   * change of VALUE, never on a change of type. `-0 === 0` is `true`, which is
   * what stops `-0.001` re-emitting forever once it has snapped to `-0`.
   *
   * 🔻 **THE KNOWN RESIDUE, DECLARED RATHER THAN FIXED (F7, 2026-09-22): a
   * caller can be left holding a >2dp STRING whose NUMBER is already 2dp** —
   * `'19.330'` rounds to `19.33`, equals its own numeric value, and is
   * therefore not re-emitted, so the caller's state keeps the trailing zero.
   * **It is left alone deliberately, on two measurements:**
   *   · the STORED value is unaffected in every case — `19.330` and `19.33`
   *     are one number to every payload builder, every JSON body and every
   *     money column, and the reviewer found no case where the stored value
   *     was wrong;
   *   · emitting on a TYPE or FORMATTING difference is the change §5.9 exists
   *     to forbid — it would rewrite a caller's `50` into `'50'` on every blur
   *     of an untouched field across ~42 call sites, trading a cosmetic
   *     residue for a dirty-guard trip on every screen.
   * 🔑 The residue is also invisible at rest: the blurred field renders
   * `formatCurrency(19.330)` = `£19.33`. **If it is ever worth closing, close
   * it by comparing the STRING the caller holds with `String(rounded)`, and
   * accept the type churn knowingly.**
   */
  const handleBlur = () => {
    setFocused(false);
    if (disabled) return;
    if (rawValue === '') return;          // BLANK STAYS BLANK.
    const rounded = roundMoney(rawValue);
    if (rounded === null) return;         // MALFORMED TEXT IS HANDED BACK.
    const numeric = toMoneyNumber(rawValue);
    if (rounded === numeric) return;      // Already 2dp — keep the caller's type.
    onChange(String(rounded));
  };

  const input = (
    <input
      id={id}
      name={name}
      type={focused ? 'number' : 'text'}
      step="0.01"
      min={allowNegative ? undefined : '0'}
      value={displayValue}
      onInput={(e) => emitTyped(e, true)}
      onChange={(e) => emitTyped(e, false)}
      onKeyDown={handleKeyDown}
      onDrop={handleDrop}
      onFocus={() => setFocused(true)}
      onBlur={handleBlur}
      placeholder={placeholder}
      className={showPrefix ? `${className || ''} pl-7`.trim() : className}
      disabled={disabled}
      data-testid={testId}
    />
  );

  // 🔑 No `prefix`, no wrapper. A bare `<input>` is what every Budget call site
  // has always rendered, and a `<div>` appearing around thirty of them would be
  // a silent layout change dressed as a refactor.
  if (!prefix) return input;

  return (
    <div className="relative">
      {showPrefix && (
        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 dark:text-gray-500 pointer-events-none">
          £
        </span>
      )}
      {input}
    </div>
  );
}

export default CurrencyInput;
